/**
 * Mobile Analytics Page
 * 
 * Mobile-optimized analytics page with filter logic similar to Activity page.
 * Clean, organized structure with extracted hooks for better maintainability.
 */

import { useState, useEffect, useMemo, useRef, ReactElement } from "react";
import { useAnalyticsTransactions } from "./Analytics/hooks/useAnalyticsTransactions";
import { useAnalyticsAccounts } from "./Analytics/hooks/useAnalyticsAccounts";
import { useAnalyticsWorkspace } from "./Analytics/hooks/useAnalyticsWorkspace";
import { useAnalyticsCategories } from "./Analytics/hooks/useAnalyticsCategories";
import { useAnalyticsCurrencies } from "./Analytics/hooks/useAnalyticsCurrencies";
import { useAnalyticsWorkspaceMembers } from "./Analytics/hooks/useAnalyticsWorkspaceMembers";
import { useAnalyticsHelpers, getCategoryColor } from "./Analytics/hooks/useAnalyticsHelpers";
import { AnalyticsPieChart, CategoryData } from "./Analytics/components/AnalyticsPieChart";
import { AnalyticsCategoryList } from "./Analytics/components/AnalyticsCategoryList";
import MobileAnalyticsFilters from "./MobileAnalytics/components/MobileAnalyticsFilters";
import TuneIcon from "@mui/icons-material/Tune";
import { useMobileAnalyticsData } from "./MobileAnalytics/hooks/useMobileAnalyticsData";
import { useMobileAnalyticsFilters } from "./MobileAnalytics/hooks/useMobileAnalyticsFilters";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

// Remember which workspaces have already played the entrance animation this session (survives tab switch)
const workspacesThatAlreadyAnimated = new Set<string>();

interface MobileAnalyticsProps {
  workspaceId: string;
  onNavigateToActivity?: (filters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  }) => void;
}

function MobileAnalytics({ workspaceId, onNavigateToActivity }: MobileAnalyticsProps): ReactElement {
  // Data fetching hooks
  const { transactions, loading: transactionsLoading } = useAnalyticsTransactions({ workspaceId });
  const { accounts } = useAnalyticsAccounts({ workspaceId });
  const { workspaceCreatedAt, defaultCurrency, defaultCurrencyLoaded } = useAnalyticsWorkspace({ workspaceId });
  const { categories, groups, loading: categoriesLoading } = useAnalyticsCategories({ workspaceId });
  const { currencies } = useAnalyticsCurrencies({ workspaceId, defaultCurrency });
  const { workspaceMembers } = useAnalyticsWorkspaceMembers({ workspaceId });

  // Helper functions
  const {
    formatAmount,
    convertToDefaultCurrency,
    getAccountName,
    getAccountDisplayName,
    getUserName,
    setDateRangeByPeriod: setDateRangeByPeriodHelper,
  } = useAnalyticsHelpers({
    currencies,
    accounts,
    workspaceMembers,
    defaultCurrency,
    workspaceCreatedAt,
    transactions,
  });

  // Filter state management
  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    selectedPeriod,
    handlePeriodChange,
    handlePeriodNavigation,
    selectedAccounts,
    setSelectedAccounts,
    selectedUsers,
    setSelectedUsers,
    filtersExpanded,
    setFiltersExpanded,
    focusedDateField,
    setFocusedDateField,
    hasActiveFilters,
    clearAllFilters,
  } = useMobileAnalyticsFilters({
    workspaceId,
    workspaceCreatedAt,
    accounts,
    workspaceMembers,
    setDateRangeByPeriod: (periodType: string, offset: number = 0) => {
      setDateRangeByPeriodHelper(periodType, offset, setFromDate, setToDate);
    },
  });

  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<"expenses" | "incomes" | "adjustments">("expenses");
  const [aggregationMode, setAggregationMode] = useState<"category" | "group">("group");
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  
  // Animation refs
  const previousDataHashRef = useRef<string>("");
  const previousTabRef = useRef<"expenses" | "incomes" | "adjustments">(selectedTab);
  const previousAggregationModeRef = useRef<"category" | "group">(aggregationMode);
  const hasAnimatedOnMountRef = useRef<boolean>(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Track stable categoryData to prevent showing intermediate states
  const stableCategoryDataRef = useRef<CategoryData[]>([]);
  const categoryDataStabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCategoryDataRef = useRef<CategoryData[]>([]);
  const [stableCategoryData, setStableCategoryData] = useState<CategoryData[]>([]);

  // Update loading state
  useEffect(() => {
    setLoading(transactionsLoading);
  }, [transactionsLoading]);

  // Reset display/stability state only when workspaceId changes so we never show wrong workspace's graph.
  // Do NOT clear when loading just finished: that caused a brief placeholder flash on re-enter when data
  // was already available (we clear stable data then 150ms later repopulate it; in between placeholder showed).
  // Keeping previous stable data until the stability effect updates it avoids the flash; workspace change still clears.
  const prevWorkspaceIdRef = useRef<string>(workspaceId);
  useEffect(() => {
    const workspaceChanged = prevWorkspaceIdRef.current !== workspaceId;
    prevWorkspaceIdRef.current = workspaceId;

    if (workspaceChanged) {
      if (categoryDataStabilityTimerRef.current) {
        clearTimeout(categoryDataStabilityTimerRef.current);
        categoryDataStabilityTimerRef.current = null;
      }
      previousCategoryDataHashRef.current = "";
      stableCategoryDataRef.current = [];
      setStableCategoryData([]);
    }
  }, [workspaceId]);

  // Track previous aggregation mode to detect changes
  // Reset animation state when aggregation mode changes to ensure smooth transition
  useEffect(() => {
    if (previousAggregationModeRef.current !== aggregationMode) {
      previousAggregationModeRef.current = aggregationMode;
      // Reset data hash to trigger animation when mode changes
      previousDataHashRef.current = "";
      // Reset stable data when mode changes
      stableCategoryDataRef.current = [];
      setStableCategoryData([]);
    }
  }, [aggregationMode]);

  // Data processing
  const { filteredTransactions, categoryData, totalAmount } = useMobileAnalyticsData({
    transactions,
    categories,
    groups,
    accounts,
    fromDate,
    toDate,
    selectedAccounts,
    selectedUsers,
    selectedTab,
    aggregationMode,
    categoriesLoading,
    defaultCurrencyLoaded,
    defaultCurrency,
    currencies,
    convertToDefaultCurrency,
    getAccountName,
  });

  // Track previous categoryData hash to detect actual changes
  const previousCategoryDataHashRef = useRef<string>("");

  // Update ref with latest categoryData
  useEffect(() => {
    latestCategoryDataRef.current = categoryData;
  }, [categoryData]);

  // Stabilize categoryData - only show data after it's been stable for 150ms
  // This prevents showing intermediate states during rapid recalculations
  // BUT: If there are no filtered transactions, immediately clear stable data to show placeholders
  // Use a stable hash to prevent unnecessary re-renders
  const categoryDataHash = useMemo(() => {
    if (filteredTransactions.length === 0) return "";
    return JSON.stringify(categoryData.map(d => ({ name: d.name, value: d.value })));
  }, [categoryData, filteredTransactions.length]);

  useEffect(() => {
    // CRITICAL: If there are no filtered transactions, immediately clear stable data
    // This prevents showing stale data when user navigates back from detail page
    if (filteredTransactions.length === 0) {
      // Clear any existing timer
      if (categoryDataStabilityTimerRef.current) {
        clearTimeout(categoryDataStabilityTimerRef.current);
        categoryDataStabilityTimerRef.current = null;
      }
      // Immediately clear stable data to show placeholders
      stableCategoryDataRef.current = [];
      setStableCategoryData([]);
      previousCategoryDataHashRef.current = "";
      return;
    }
    
    // Only proceed if data actually changed
    if (categoryDataHash === previousCategoryDataHashRef.current) {
      return; // No change, don't do anything
    }
    
    // Update the hash ref
    const hadNoStableDataBefore = previousCategoryDataHashRef.current === "";
    previousCategoryDataHashRef.current = categoryDataHash;
    
    // Clear any existing timer
    if (categoryDataStabilityTimerRef.current) {
      clearTimeout(categoryDataStabilityTimerRef.current);
      categoryDataStabilityTimerRef.current = null;
    }
    
    // When re-entering with data (had no stable data, now we have data), apply immediately to avoid ~100ms placeholder flash.
    // When user changes filter/tab (already had data), keep 150ms delay to avoid intermediate flicker.
    const delayMs = hadNoStableDataBefore ? 0 : 150;
    categoryDataStabilityTimerRef.current = setTimeout(() => {
      // Get latest categoryData from ref
      const latestData = latestCategoryDataRef.current;
      // Double-check the hash is still the same (data hasn't changed again)
      const currentHash = JSON.stringify(latestData.map(d => ({ name: d.name, value: d.value })));
      if (currentHash === categoryDataHash) {
        stableCategoryDataRef.current = latestData;
        setStableCategoryData([...latestData]); // Create new array to trigger update
      }
      categoryDataStabilityTimerRef.current = null;
    }, delayMs);

    return () => {
      if (categoryDataStabilityTimerRef.current) {
        clearTimeout(categoryDataStabilityTimerRef.current);
        categoryDataStabilityTimerRef.current = null;
      }
    };
  }, [categoryDataHash, filteredTransactions.length]);

  // Use stable categoryData for rendering, but fallback to categoryData if stable is empty
  const displayCategoryData = useMemo(() => {
    if (stableCategoryData.length > 0) {
      return stableCategoryData;
    }
    // On initial render or when stable data is empty, use categoryData directly
    return categoryData;
  }, [stableCategoryData, categoryData]);

  // Generate placeholder items based on actual user data (groups, categories, or accounts)
  const placeholderItems = useMemo((): string[] => {
    if (selectedTab === "adjustments") {
      // For adjustments, use actual account names
      return accounts
        .filter((acc) => selectedAccounts.length === 0 || selectedAccounts.includes(acc.id))
        .map((acc) => acc.accountName)
        .slice(0, 6);
    } else {
      // For expenses and incomes, use groups or categories based on aggregationMode
      const type = selectedTab === "expenses" ? "expense" : "income";
      
      if (aggregationMode === "group") {
        // Use actual group names
        return groups
          .filter((g) => g.type === type)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((g) => g.name)
          .slice(0, 8);
      } else {
        // Use actual category names
        return categories
          .filter((c) => c.type === type)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((c) => c.name)
          .slice(0, selectedTab === "expenses" ? 8 : 5);
      }
    }
  }, [selectedTab, aggregationMode, groups, categories, accounts, selectedAccounts]);

  // Determine if we're currently showing placeholder data (for chart rendering)
  const isShowingPlaceholder = useMemo(() => {
    // CRITICAL: If there are no filtered transactions, always show placeholder
    // This prevents showing stale data when user navigates back from detail page
    if (filteredTransactions.length === 0) {
      return true;
    }
    
    return displayCategoryData.length === 0;
  }, [displayCategoryData.length, filteredTransactions.length]);

  // Generate pie chart data with placeholder support
  const pieChartData = useMemo(() => {
    // CRITICAL: If there are no filtered transactions, always show placeholder
    // This prevents showing stale data when user navigates back from detail page
    if (filteredTransactions.length === 0) {
      if (placeholderItems.length === 0) {
        return [{ name: "placeholder", value: 1, color: "#e0e0e0" }];
      }
      
      return placeholderItems.map((name, index) => ({
        name,
        value: 0.01, // Very small values to show as empty state
        color: getCategoryColor(index),
      }));
    }
    
    if (displayCategoryData.length === 0) {
      // Create placeholder data with equal segments for empty state
      if (placeholderItems.length === 0) {
        return [{ name: "placeholder", value: 1, color: "#e0e0e0" }];
      }
      
      // Create equal segments with placeholder colors
      return placeholderItems.map((name, index) => ({
        name,
        value: 1, // Equal values for equal segments
        color: getCategoryColor(index),
      }));
    }
    
    if (selectedTab === "adjustments") {
      return displayCategoryData.map(entry => ({ ...entry, value: Math.abs(entry.value) }));
    }
    return displayCategoryData;
  }, [displayCategoryData, filteredTransactions.length, selectedTab, placeholderItems, getCategoryColor]);

  // Track data changes to trigger animation
  // Include aggregationMode in hash to detect mode changes
  const dataHash = useMemo(() => {
    return `${selectedTab}-${aggregationMode}-${pieChartData.map(d => `${d.name}:${d.value}`).join('|')}`;
  }, [pieChartData, selectedTab, aggregationMode]);

  // Animation trigger - only once per workspace per session, and only when we have real data
  // (avoids "consuming" the one-time animation with placeholder when entering during load)
  // NOTE: Set hasAnimatedOnMountRef ONLY inside the timer callback, not before - otherwise React
  // Strict Mode cleanup clears the timer and we never call setShouldAnimate, leaving us stuck.
  useEffect(() => {
    if (loading) return;
    if (filteredTransactions.length === 0) return;

    // Already animated for this workspace this session (e.g. user switched to Activity and back)
    if (workspacesThatAlreadyAnimated.has(workspaceId)) {
      hasAnimatedOnMountRef.current = true;
      return;
    }
    if (hasAnimatedOnMountRef.current) return;

    if (!pieChartData || pieChartData.length === 0) return;

    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    previousDataHashRef.current = dataHash;
    previousTabRef.current = selectedTab;

    // Delay animation so chart container has layout (avoids Recharts -1 size and ensures animation runs when page is visible)
    const animationStartDelay = 150;
    animationTimerRef.current = setTimeout(() => {
      // Set refs here so we don't "consume" the slot if Strict Mode cleanup cancels the timer
      hasAnimatedOnMountRef.current = true;
      workspacesThatAlreadyAnimated.add(workspaceId);
      requestAnimationFrame(() => {
        setShouldAnimate(true);
        animationTimerRef.current = setTimeout(() => {
          setShouldAnimate(false);
          animationTimerRef.current = null;
        }, 600) as ReturnType<typeof setTimeout>;
      });
    }, animationStartDelay) as ReturnType<typeof setTimeout>;

    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, [loading, workspaceId, filteredTransactions.length]); // eslint-disable-line react-hooks/exhaustive-deps -- pieChartData/dataHash intentionally excluded to run only once when real data is ready

  // Calculate amounts for expenses and incomes tabs (filtered by date/accounts/users but not by tab)
  // Only show amounts when currencies are ready to avoid wrong intermediate values (e.g. 63k then 549k)
  const expensesAndIncomesAmounts = useMemo(() => {
    if (!defaultCurrencyLoaded || currencies.length === 0) {
      return { expensesAmount: 0, incomesAmount: 0 };
    }
    const selectedUsersSet = new Set(selectedUsers);
    const selectedAccountsSet = new Set(selectedAccounts);

    const baseFiltered = transactions.filter((transaction) => {
      // Date range filter
      if (fromDate || toDate) {
        const transactionDate = transaction.date;
        if (!transactionDate) return false;
        const transactionDateStr = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}-${String(transactionDate.getDate()).padStart(2, '0')}`;
        if (fromDate && transactionDateStr < fromDate) return false;
        if (toDate && transactionDateStr > toDate) return false;
      }
      // Account filter
      if (selectedAccounts.length > 0 && transaction.accountId) {
        if (!selectedAccountsSet.has(transaction.accountId)) return false;
      }
      // User filter
      if (selectedUsers.length > 0 && transaction.userId) {
        if (!selectedUsersSet.has(transaction.userId)) return false;
      }
      return true;
    });

    const expensesAmount = baseFiltered
      .filter(t => t.type === "expense")
      .reduce((sum, t) => sum + convertToDefaultCurrency(t.amount || 0, t.currency), 0);

    const incomesAmount = baseFiltered
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + convertToDefaultCurrency(t.amount || 0, t.currency), 0);

    return { expensesAmount, incomesAmount };
  }, [transactions, fromDate, toDate, selectedAccounts, selectedUsers, convertToDefaultCurrency, defaultCurrencyLoaded, currencies.length]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", bgcolor: "background.default" }}>
      {/* Filters - show immediately so tab feels instant */}
      <MobileAnalyticsFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        selectedAccounts={selectedAccounts}
        onSelectedAccountsChange={setSelectedAccounts}
        selectedUsers={selectedUsers}
        onSelectedUsersChange={setSelectedUsers}
        focusedDateField={focusedDateField}
        onFocusedDateFieldChange={setFocusedDateField}
        onPeriodNavigation={handlePeriodNavigation}
        accounts={accounts}
        workspaceMembers={workspaceMembers}
        getAccountDisplayName={getAccountDisplayName}
        getUserName={getUserName}
        filtersExpanded={filtersExpanded}
        onFiltersExpandedChange={setFiltersExpanded}
        hasActiveFilters={hasActiveFilters}
        onClearAllFilters={clearAllFilters}
      />

      {/* Header with Tabs and Aggregation Toggle */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 1.5, 
          mb: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          userSelect: "none", 
          position: "sticky", 
          top: { xs: "0px", sm: "0px" }, 
          zIndex: 12, // Increased z-index to ensure tabs are always clickable above chart
          pointerEvents: "auto", // Ensure pointer events work
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pointerEvents: "auto" }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons={false}
            sx={{ 
              borderBottom: 1, 
              borderColor: "divider", 
              minHeight: "auto", 
              userSelect: "none", 
              mb: 1,
              pointerEvents: "auto", // Ensure tabs are clickable during animation
              "& .MuiTabs-flexContainer": {
                justifyContent: "space-between",
              },
              "& .MuiTab-root": {
                pointerEvents: "auto", // Ensure individual tabs are clickable
              },
              "& .MuiTab-root:nth-of-type(1)": {
                // Expenses - add spacing after to separate from Incomes
                marginRight: 1.5,
              },
              "& .MuiTab-root:nth-of-type(2)": {
                // Incomes - add gap before Adjustments
                marginRight: "auto",
              },
              "& .MuiTab-root:nth-of-type(3)": {
                // Adjustments - separate on the right
                marginLeft: "auto",
                borderLeft: "1px solid",
                borderColor: "divider",
                paddingLeft: 1.5,
              },
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
                    Expenses
                  </Typography>
                  <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem", opacity: 0.85, color: "text.secondary" }}>
                    {formatAmount(expensesAndIncomesAmounts.expensesAmount)}
                  </Typography>
                </Box>
              }
              value="expenses" 
              sx={{ py: 1, minHeight: "auto", userSelect: "none", textTransform: "none", pointerEvents: "auto" }} 
            />
            <Tab 
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
                    Incomes
                  </Typography>
                  <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem", opacity: 0.85, color: "text.secondary" }}>
                    {formatAmount(expensesAndIncomesAmounts.incomesAmount)}
                  </Typography>
                </Box>
              }
              value="incomes" 
              sx={{ py: 1, minHeight: "auto", userSelect: "none", textTransform: "none", pointerEvents: "auto" }} 
            />
            <Tab 
              icon={<TuneIcon sx={{ fontSize: "1.1rem", opacity: 0.7 }} />}
              value="adjustments" 
              sx={{ 
                py: 1, 
                minHeight: "auto", 
                userSelect: "none",
                minWidth: "auto",
                px: 1.5,
                pointerEvents: "auto",
              }} 
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Scrollable Chart and Category List */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", userSelect: "none" }}>
        {/* Graph card */}
        <Paper elevation={1} sx={{ p: 1.5, mb: 1.5, borderTopLeftRadius: 0, borderTopRightRadius: 0, display: "flex", flexDirection: "column", userSelect: "none", position: "relative" }}>
          {/* Aggregation Mode Toggle - Top left of graph card, only for expenses/incomes */}
          {selectedTab !== "adjustments" && (
            <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
              <ToggleButtonGroup
                value={aggregationMode}
                exclusive
                onChange={(_, newValue) => newValue && setAggregationMode(newValue)}
                size="small"
                sx={{ 
                  userSelect: "none",
                  height: "24px",
                  "& .MuiToggleButton-root": {
                    px: 1,
                    py: 0.25,
                    fontSize: "0.7rem",
                    border: 1,
                    borderColor: "divider",
                    textTransform: "none",
                    minWidth: "auto",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    },
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  },
                }}
              >
                <ToggleButton value="group" sx={{ userSelect: "none" }}>Group</ToggleButton>
                <ToggleButton value="category" sx={{ userSelect: "none" }}>Category</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Pie Chart - defer heavy chart until data ready so tab opens fast */}
            <Box 
              sx={{ 
                width: "100%", 
                height: { xs: "280px", sm: "320px" }, 
                minHeight: { xs: "280px", sm: "320px" },
                position: "relative",
                overflow: "hidden",
              }}
            >
              {(() => {
                // Defer chart mount until shouldAnimate is true (first-time) so pie animates on load.
                // Return visitors (workspacesThatAlreadyAnimated) see chart immediately.
                // When isShowingPlaceholder (no transactions), render the colorful pie placeholder - not empty box.
                const waitingForFirstAnimation =
                  filteredTransactions.length > 0 &&
                  !isShowingPlaceholder &&
                  !shouldAnimate &&
                  !workspacesThatAlreadyAnimated.has(workspaceId);
                // When we have data but categoryData not ready yet, show neutral box (no placeholder flash for ~100ms)
                const dataNotReadyYet = filteredTransactions.length > 0 && displayCategoryData.length === 0;
                const showEmptyBox = loading || waitingForFirstAnimation || dataNotReadyYet;
                return showEmptyBox ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    aria-hidden
                  >
                    {loading && <CircularProgress size={24} sx={{ color: "text.secondary" }} />}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <AnalyticsPieChart
                      data={pieChartData}
                      selectedTab={selectedTab}
                      isShowingPlaceholder={isShowingPlaceholder}
                      shouldHidePlaceholder={false}
                      shouldAnimate={shouldAnimate}
                      loading={false}
                      formatAmount={formatAmount}
                      categoryData={displayCategoryData}
                      fitContainer
                    />
                  </Box>
                );
              })()}
            </Box>
          </Box>
        </Paper>

        {/* Category / Group list card */}
        <Paper elevation={1} sx={{ p: 1.5, mt: "10px", mb: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0, display: "flex", flexDirection: "column", userSelect: "none" }}>
          <AnalyticsCategoryList
            key={`category-list-${selectedTab}-${aggregationMode}-${fromDate}-${toDate}`}
            selectedTab={selectedTab}
            isShowingPlaceholder={isShowingPlaceholder}
            categoryData={displayCategoryData}
            totalAmount={totalAmount}
            formatAmount={formatAmount}
            getCategoryColor={getCategoryColor}
            placeholderItems={placeholderItems}
            accounts={accounts}
            selectedAccounts={selectedAccounts}
            selectedUsers={selectedUsers}
            workspaceMembersLength={workspaceMembers.length}
            fromDate={fromDate}
            toDate={toDate}
            selectedPeriod={selectedPeriod}
            onNavigateToActivity={onNavigateToActivity}
            aggregationMode={aggregationMode}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export default MobileAnalytics;
